import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowRight, Briefcase, Code, Shield, Bot } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const jobRoles = [
  {
    id: 1,
    title: "Backend Engineer",
    icon: Code,
    description: "Design and develop scalable backend systems and APIs",
    skills: ["Node.js", "Python", "MongoDB", "PostgreSQL", "REST APIs"]
  },
  {
    id: 2,
    title: "DevOps Security Engineer",
    icon: Shield,
    description: "Manage infrastructure, CI/CD pipelines, and security protocols",
    skills: ["AWS", "Docker", "Kubernetes", "Security", "Linux"]
  },
  {
    id: 3,
    title: "Full Stack Developer",
    icon: Code,
    description: "Build end-to-end web applications with modern technologies",
    skills: ["React", "Node.js", "MongoDB", "TypeScript", "AWS"]
  },
  {
    id: 4,
    title: "AI Automation Engineer",
    icon: Bot,
    description: "Build and deploy intelligent AI agents and automation workflows",
    skills: ["Python", "LLMs", "LangChain", "Machine Learning", "API Integration"]
  }
];

const Careers = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    experience: '',
    portfolio: '',
    message: '',
    honeypot: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    document.title = 'Careers at Sikka IT Solutions | Join Our Team in Lucknow';
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Bot protection - honeypot field
    if (formData.honeypot) {
      toast.error("Submission failed", {
        description: "Please try again later.",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Remove honeypot from submission
      const { honeypot, ...submitData } = formData;
      
      await axios.post(`${API}/careers`, submitData);
      
      toast.success("Application submitted successfully!", {
        description: "We'll review your application and get back to you soon.",
      });
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        role: '',
        experience: '',
        portfolio: '',
        message: '',
        honeypot: ''
      });
    } catch (error) {
      console.error('Error submitting application:', error);
      
      if (error.response?.status === 429) {
        toast.error("Too many submissions!", {
          description: "Please wait a few minutes before trying again.",
        });
      } else {
        toast.error("Something went wrong!", {
          description: "Please try again or contact us directly.",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center">
              <img 
                src={require('../assets/sikka-logo.jpg')} 
                alt="Sikka IT Solutions" 
                className="h-12 w-auto object-contain"
              />
            </a>
            <nav className="hidden md:flex space-x-6">
              <a href="/#services" className="text-slate-700 hover:text-blue-600 transition-colors">Services</a>
              <a href="/#portfolio" className="text-slate-700 hover:text-blue-600 transition-colors">Portfolio</a>
              <a href="/#pricing" className="text-slate-700 hover:text-blue-600 transition-colors">Pricing</a>
              <a href="/careers" className="text-blue-600 font-medium transition-colors">Careers</a>
              <a href="/#contact" className="text-slate-700 hover:text-blue-600 transition-colors">Contact</a>
            </nav>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => window.location.href = '/'}>
              Back to Home
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-cyan-50 to-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <Badge className="mb-4 bg-blue-100 text-blue-700">Join Our Team</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Build Your Career at
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500"> Sikka IT Solutions</span>
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              We're looking for talented individuals who are passionate about technology and innovation. 
              Join us to work on exciting projects and grow your career.
            </p>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Open Positions
            </h2>
            <p className="text-lg text-slate-600">
              Explore opportunities to work with cutting-edge technologies
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {jobRoles.map((role) => {
              const Icon = role.icon;
              return (
                <Card key={role.id} className="hover:shadow-lg transition-shadow border-slate-200">
                  <CardHeader>
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <CardTitle className="text-xl">{role.title}</CardTitle>
                    <CardDescription className="text-slate-600">{role.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {role.skills.map((skill, idx) => (
                        <Badge key={idx} variant="outline" className="bg-slate-50">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Application Form */}
          <div className="max-w-3xl mx-auto">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-2xl">Apply Now</CardTitle>
                <CardDescription>Fill out the form below to submit your application</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">
                        Full Name *
                      </label>
                      <Input 
                        name="name"
                        placeholder="John Doe" 
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">
                        Email *
                      </label>
                      <Input 
                        name="email"
                        type="email" 
                        placeholder="john@example.com" 
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">
                        Phone Number *
                      </label>
                      <Input 
                        name="phone"
                        type="tel" 
                        placeholder="9876543210" 
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">
                        Applying For *
                      </label>
                      <select 
                        name="role"
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={formData.role}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Position</option>
                        <option value="backend-engineer">Backend Engineer</option>
                        <option value="devops-security-engineer">DevOps Security Engineer</option>
                        <option value="full-stack-developer">Full Stack Developer</option>
                        <option value="ai-automation-engineer">AI Automation Engineer</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">
                      Years of Experience *
                    </label>
                    <Input 
                      name="experience"
                      placeholder="e.g., 2 years" 
                      value={formData.experience}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">
                      Portfolio/LinkedIn/GitHub URL
                    </label>
                    <Input 
                      name="portfolio"
                      type="url"
                      placeholder="https://linkedin.com/in/yourprofile" 
                      value={formData.portfolio}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">
                      Why do you want to join us? *
                    </label>
                    <Textarea 
                      name="message"
                      placeholder="Tell us about yourself and why you'd be a great fit..." 
                      rows={4}
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  {/* Honeypot field - hidden from users */}
                  <input
                    type="text"
                    name="honeypot"
                    value={formData.honeypot}
                    onChange={handleInputChange}
                    style={{ display: 'none' }}
                    tabIndex="-1"
                    autoComplete="off"
                  />

                  <Button 
                    type="submit" 
                    className="w-full bg-blue-600 hover:bg-blue-700" 
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Application'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>

                  <p className="text-xs text-slate-500 text-center mt-4">
                    By submitting this form, you agree to our privacy policy and terms of service.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Why Join Sikka IT Solutions?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="text-center border-slate-200">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Briefcase className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Exciting Projects</h3>
                <p className="text-slate-600">
                  Work on diverse projects using the latest technologies
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border-slate-200">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Code className="h-8 w-8 text-cyan-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Growth Opportunities</h3>
                <p className="text-slate-600">
                  Continuous learning and career development programs
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border-slate-200">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Great Culture</h3>
                <p className="text-slate-600">
                  Collaborative environment with work-life balance
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
